import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material';
import React, { forwardRef, useEffect, useRef } from 'react';

export interface CustomButtonProps extends Omit<MuiButtonProps, 'children'> {
  loading?: boolean;
  /** When set, spinner + this text are shown inline instead of hiding children. */
  loadingText?: React.ReactNode;
  children?: React.ReactNode | React.ReactNode[];
  component?: React.ElementType;
  [key: string]: any;
}

const CLICK_GUARD_FALLBACK_MS = 500;
const GUARDED_VARIANTS = new Set(['contained', 'outlined', 'dashed']);

const Button = forwardRef<HTMLButtonElement, CustomButtonProps>(
  function Button({ loading = false, loadingText, disabled, children, variant, onClick, ...rest }, ref) {
    const spinnerColor = variant === 'contained' ? '#ffffff' : '#4f9b44';
    const guardEnabled = GUARDED_VARIANTS.has(variant as string);
    // Ref-pohjainen guard: ei triggeröi re-renderiä eikä muuta disabled-attribuuttia,
    // joten type="submit"-napit eivät esty selaintasolla
    const guardRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const loadingRef = useRef(loading);
    const prevLoadingRef = useRef(loading);
    loadingRef.current = loading;

    // Kun loading päättyy (true → false), vapautetaan guard
    useEffect(() => {
      if (prevLoadingRef.current && !loading) {
        guardRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
      }
      prevLoadingRef.current = loading;
    }, [loading]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (guardEnabled && guardRef.current) return;
      if (guardEnabled) {
        guardRef.current = true;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          if (!loadingRef.current) guardRef.current = false;
        }, CLICK_GUARD_FALLBACK_MS);
      }
      onClick?.(e);
    };

    return (
      <MuiButton
        ref={ref}
        variant={variant}
        disabled={disabled || loading}
        onClick={handleClick}
        {...rest}
        sx={{ position: 'relative', ...rest.sx }}
      >
        {loading && loadingText ? (
          <>
            <CircularProgress size={20} sx={{ color: spinnerColor, mr: 1 }} />
            {loadingText}
          </>
        ) : (
          <>
            {loading && (
              <CircularProgress
                size={20}
                sx={{
                  color: spinnerColor,
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  ml: '-10px',
                  mt: '-10px',
                }}
              />
            )}
            <span style={{ visibility: loading ? 'hidden' : 'visible', display: 'inherit', alignItems: 'inherit', justifyContent: 'inherit' }}>{children}</span>
          </>
        )}
      </MuiButton>
    );
  }
);

export default Button;
